/**
 * Continents — chalk-stroked geographic continent silhouettes layered over
 * the slate ocean. Three responsibilities:
 *
 *   1. Backplate: paint each continent with a faint paper-cream wash so it
 *      reads as land mass against the slate ocean.
 *   2. Coastline: stroke the silhouette with a chalk-roughened outline.
 *   3. ClipPath defs: emit one `<clipPath>` per continent so MapView can
 *      clip Voronoi-derived territory polygons to the actual coastline.
 *
 * Only continents that contain at least one *active* module are rendered —
 * a 2-player game (frontend + backend) shows just NA + Asia.
 */

import { type ReactNode, useMemo } from 'react';
import { CONTINENTS, type ModuleId } from '@qd/map';
import { surfaces } from '@qd/design-tokens';
import { CONTINENT_OFFSETS } from './MapOffsets.js';

interface ContinentsProps {
  /** Module ids active in the current map (filters out empty continents). */
  readonly activeModuleIds: ReadonlySet<ModuleId>;
}

/** SVG filter id from filters/index.tsx; falls back gracefully if missing. */
const CHALK_FILTER_ID = 'qd-filter-chalk-roughen';

/** Clean paper-white land wash sitting over the vibrant ocean. */
const LAND_FILL = surfaces.paper;
const LAND_FILL_OPACITY = 0.7;

/** Coastline stroke colors. */
const COAST_STROKE = surfaces.chalk;
/** Warm earth halo just outside the ink edge — reads as a vintage map's
 *  hand-painted shoreline glow on parchment. */
const COAST_HALO = '#8A6A3F';

export function Continents({ activeModuleIds }: ContinentsProps): ReactNode {
  const visibleContinents = useMemo(
    () =>
      CONTINENTS.filter((c) =>
        c.modulesPresent.some((m) => activeModuleIds.has(m)),
      ),
    [activeModuleIds],
  );

  return (
    <>
      {/* clipPath defs — referenced by territory polygons in MapView. */}
      <defs>
        {visibleContinents.map((c) => {
          return (
            <clipPath
              key={`clip-${c.id}`}
              id={`qd-continent-clip-${c.id}`}
              clipPathUnits="userSpaceOnUse"
            >
              <path d={c.pathD} />
            </clipPath>
          );
        })}
      </defs>

      <g data-layer="continents">
        {/* Pass 0 — concentric "tide" rings echoing the coastline outward
            into the parchment ocean. Faint warm earth tone so each landmass
            sits inside its own hand-drawn aura. */}
        {[
          { width: 16, opacity: 0.05 },
          { width: 10, opacity: 0.09 },
          { width: 6, opacity: 0.16 },
        ].map(({ width, opacity }) => (
          <g key={`bathy-${width}`}>
            {visibleContinents.map((c) => {
              const offset = CONTINENT_OFFSETS[c.id] || { dx: 0, dy: 0 };
              return (
                <path
                  key={`bathy-${c.id}-${width}`}
                  d={c.pathD}
                  fill="none"
                  stroke={COAST_HALO}
                  strokeWidth={width}
                  strokeOpacity={opacity}
                  strokeLinejoin="round"
                  strokeLinecap="round"
                  transform={`translate(${offset.dx}, ${offset.dy})`}
                />
              );
            })}
          </g>
        ))}

        {/* Pass 1 — earth-toned halo just outside the coastline so the land
            edge reads as a hand-painted shoreline. */}
        <g filter={`url(#${CHALK_FILTER_ID})`}>
          {visibleContinents.map((c) => {
            const offset = CONTINENT_OFFSETS[c.id] || { dx: 0, dy: 0 };
            return (
              <path
                key={`halo-${c.id}`}
                d={c.pathD}
                fill="none"
                stroke={COAST_HALO}
                strokeWidth={3}
                strokeOpacity={0.55}
                strokeLinejoin="round"
                strokeLinecap="round"
                transform={`translate(${offset.dx}, ${offset.dy})`}
              />
            );
          })}
        </g>

        {/* Pass 2 — paper-cream land wash. */}
        {visibleContinents.map((c) => {
          const offset = CONTINENT_OFFSETS[c.id] || { dx: 0, dy: 0 };
          return (
            <path
              key={`fill-${c.id}`}
              d={c.pathD}
              fill={LAND_FILL}
              fillOpacity={LAND_FILL_OPACITY}
              stroke="none"
              transform={`translate(${offset.dx}, ${offset.dy})`}
            />
          );
        })}

        {/* Pass 3 — chalk coastline (the iconic Risk-board outline). */}
        <g filter={`url(#${CHALK_FILTER_ID})`}>
          {visibleContinents.map((c) => {
            const offset = CONTINENT_OFFSETS[c.id] || { dx: 0, dy: 0 };
            return (
              <path
                key={`coast-${c.id}`}
                d={c.pathD}
                fill="none"
                stroke={COAST_STROKE}
                strokeWidth={1.6}
                strokeOpacity={0.85}
                strokeLinejoin="round"
                strokeLinecap="round"
                transform={`translate(${offset.dx}, ${offset.dy})`}
              />
            );
          })}
        </g>

        {/* Pass 4 — continent labels in vintage caps, anchored at labelAnchor. */}
        {visibleContinents.map((c) => {
          const offset = CONTINENT_OFFSETS[c.id] || { dx: 0, dy: 0 };
          return (
            <text
              key={`label-${c.id}`}
              x={c.labelAnchor.x + offset.dx}
              y={c.labelAnchor.y + offset.dy}
              textAnchor="middle"
              fill={surfaces.chalk}
              fillOpacity={0.6}
              fontSize={32}
              fontFamily="'Fraunces', Georgia, serif"
              letterSpacing="0.32em"
              fontWeight={500}
              pointerEvents="none"
              style={{ textTransform: 'uppercase' }}
            >
              {c.displayName.toUpperCase()}
            </text>
          );
        })}
      </g>
    </>
  );
}
